/**
 * Created by Maxim B. on 20/04/20.
 * Copyright © 2020 Curriculum Ltd. All rights reserved.
 */

import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ITableStructure } from './students-statistic-table.interface';
import { MatTabGroup, MatTabNav } from '@angular/material/tabs';
import { MatDialog } from '@angular/material/dialog';
import { StatisticRequestParams, StatisticTableService } from './statistic-table.service';
import { ToastrService } from 'ngx-toastr';
import {
  CoursesFilterOptions,
  CoursesFilterFields,
  SelectCoursesFiltersComponent,
} from '../select-courses-filters/select-courses-filters.component';

const tableFields: ITableStructure = {
  fields: [
    { fieldName: 'hwCompletionRate', fieldFilter: 'all' },
    { fieldName: 'qzCompletionRate', fieldFilter: 'all' },
    { fieldName: 'ctCompletionRate', fieldFilter: 'all' },
    { fieldName: 'latestCt', fieldFilter: 'all' },
    { fieldName: 'avgGrade', fieldFilter: 'all' },
    { fieldName: 'performance', fieldFilter: 'all' },
    // { fieldName: 'prevWeekMeetings', fieldFilter: 'all' },
  ],
  firstColumn: { fieldName: 'name', fieldFilter: '' },
  name: 'Students',
};

@Component({
  selector: 'curr-students-statistic-table',
  templateUrl: './students-statistic-table.component.html',
  styleUrls: ['./students-statistic-table.component.scss'],
})
export class StudentsStatisticTableComponent implements OnInit, OnDestroy {
  @Input() statistic: [];
  @Input() averageBenchmark: string;
  @Input() set filtersData(filters: {subject: string, studyYearId: number, courseId: number, schoolYearSettingsId?: number}) {
    if (filters) {
      this.selectFilter[this.coursesFilterFields.subject] = filters.subject;
      this.selectFilter[this.coursesFilterFields.grade] = filters.studyYearId;
      this.selectFilter[this.coursesFilterFields.course] = filters.courseId;
      if (filters.schoolYearSettingsId) this.selectFilter[this.coursesFilterFields.schoolYearSettingsId] = filters.schoolYearSettingsId;
      this.getStudentStatistic();
    }
  }
  @Input() showFullList = false;

  @ViewChild(SelectCoursesFiltersComponent) tableFiltersRef: SelectCoursesFiltersComponent;

  searchValue = '';
  isMeetingThisWeek = true;
  isMeetingDropdownOpen = false;
  isInTableDropdownOpen = false;
  clickedPendingUser = '';
  pendingMenuSubscription: any;
  isDataLoading = false;
  isDataPresented = true;

  resultsLength = 0;
  currentPageIndex = 0
  customTables: ITableStructure = tableFields;

  @ViewChild('nameInput') private nameInput: ElementRef;
  @ViewChild(MatTabGroup) matTabs: MatTabNav;

  // FILTERS - DROPDOWN
  ddOptionsPredictedGrade = [];
  selectFilter: CoursesFilterOptions = {};
  filterData: any;
  coursesFilterFields: CoursesFilterFields = {
    subject: 'subjectName',
    course: 'stCourseId',
    grade: 'studyYearId',
    schoolYearSettingsId: 'schoolYearSettingsId',
  };

  // TABLE
  STUDENTS_DATA = [];
  ELEMENT_DATA = [];
  displayedColumns: string[] = [];
  dataSource: any;

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(
    private cdr: ChangeDetectorRef,
    private toastr: ToastrService,
    public dialog: MatDialog,
    public statisticTableService: StatisticTableService,
  ) {}

  ngOnInit(): void {
    this.initTableData();
    this.getOptionsForFilters();
  }

  onPageChange(pageIndex: number) {
    this.getStudentStatistic(pageIndex * 20);
  }

  initTableData() {
    if (this.showFullList) this.getStudentStatistic();

    this.ddOptionsPredictedGrade = [
      { value: 'A', label: 'A' },
      { value: 'B', label: 'B' },
      { value: 'C', label: 'C' },
      { value: 'D', label: 'D' },
      { value: 'E', label: 'E' },
      { value: 'F', label: 'F' }
    ];
  }

  updateCurrentTable() {
    this.getStudentStatistic();
  }

  getStudentStatistic(offset = 0) {
    this.showSpinner();
    const o: StatisticRequestParams = {
      rowsCount: 20,
      rowOffset: offset,
      ...this.selectFilter
    };

    this.statisticTableService.getStudentsStatistic(o).subscribe(
      (res: any) => {
        if (!res.results.length) {
          // this.toastr.info('No data to show');
          this.isDataPresented = false;
          return;
        } else {
          this.isDataPresented = true;
          this.resultsLength = res.totalResults;
          this.currentPageIndex = res.pageIndex;

          res.results = res.results.map((item, key) => {
            item.hwCompletionRate = `${item.hwCompletionRate}%`;
            item.qzCompletionRate = `${item.qzCompletionRate}%`;
            item.ctCompletionRate = `${item.ctCompletionRate}%`;
            return item;
          })
          this.STUDENTS_DATA = [];
          this.STUDENTS_DATA.push(...res.results);
          this.setStudentsTableData();
        }
      },
      error => {
        this.toastr.error('Failed to load statistic');
      },
      () => {
        this.hideSpinner();
      }
    );
  }

  setStudentsTableData() {
    this.displayedColumns = ['name'];
    this.customTables.fields.forEach((i) => {
      this.displayedColumns.push(i.fieldName + '');
    });
    this.dataSource = new MatTableDataSource(this.STUDENTS_DATA);
    this.cdr.detectChanges();
    // this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.setFilterPredicate();
  }

  setFilterPredicate() {
    this.dataSource.filterPredicate = (data, filter: string) => {
      return (
        data.name.toLowerCase().indexOf(filter) > -1 ||
        data.subject?.toLowerCase().indexOf(this.selectFilter.subjectName) > -1 ||
        (data.year !== undefined && data.year === this.selectFilter.studyYearId) ||
        (data.form !== undefined && data.form === this.selectFilter.stCourseId)
      );
    };
  }

  filterEntries(event: any) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchValue = filterValue.trim().toLowerCase();
    this.dataSource.filter = this.searchValue.length ? this.searchValue : '__________';
  }

  onSelectFilterChange(selectFilter: CoursesFilterOptions) {
    this.selectFilter = selectFilter;

    this.setFilterPredicate();
    this.dataSource.filter = this.searchValue.length ? this.searchValue : '__________'; // If empty string filter won't apply
    this.updateCurrentTable();
  }

  selectMeetingWeek(b: boolean) {
    this.isMeetingDropdownOpen = false;
    this.isMeetingThisWeek = b;
    this.dataSource.filterPredicate = (data, filter: string) => {
      return data.meetings.toLowerCase().includes(filter);
    };
    this.dataSource.filter = this.isMeetingThisWeek ? 'yes' : 'pending';
  }

  getOptionsForFilters() {
    this.statisticTableService.getFilters().subscribe((resp) => {
      this.filterData = resp;
    });
  }

  showSpinner() {
    this.isDataLoading = true;
  }

  hideSpinner() {
    this.isDataLoading = false;
  }

  setMeetingToDone(element: any, status: string) {
    const st = this.isMeetingThisWeek ? 'THIS_WEEK' : 'PREV_WEEK';
    this.isInTableDropdownOpen = false;
    // this.statisticTableService.setMeeting(element.courseStudentId, st).subscribe((resp) => {
    //   element.prevWeekMeetings = status;
    // });
  }

  clickPending($event: any, elem: any, menu: any) {
    this.clickedPendingUser = elem.name;
    $event.stopPropagation();
    this.isInTableDropdownOpen = true;
    this.pendingMenuSubscription = menu.close.subscribe((i) => {
      this.clickedPendingUser = '';
      this.isInTableDropdownOpen = false;
    });
  }

  ngOnDestroy(): void {
    if (this.pendingMenuSubscription) {
      this.pendingMenuSubscription.unsubscribe();
    }
  }

  courseIdChanged(courseId: string) {
    // this.statisticTableService.courseId = courseId;
    this.initTableData();
  }
}
